import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    questionId: Number,
    questionType: String,
    questionNumber: Number,
    teamId: Number,
    profileId: Number
  }

  connect() {
    this.currentAnswer = null
    this.isProcessing = false
    this.isInitializing = false

    // Initialize based on question type
    if (this.questionTypeValue === "ranking") {
      this.initializeRanking()
    } else if (this.questionTypeValue === "slider") {
      this.initializeSliders()
    } else if (this.questionTypeValue === "scale") {
      this.initializeScale()
    } else if (this.questionTypeValue === "multiselect") {
      this.initializeMultiselect()
    } else if (this.questionTypeValue === "radio") {
      this.initializeRadio()
    }

    // Update button state after initialization
    setTimeout(() => {
      this.updateNextButton()
    }, 100)
  }

  // Q1: Ranking (drag-and-drop)
  initializeRanking() {
    const container = this.element.querySelector("#ranking-container")
    if (!container) {
      console.error("Ranking container not found")
      return
    }

    let draggedElement = null
    let draggedOverElement = null
    this.isInitializing = true

    const rankingItems = container.querySelectorAll(".ranking-item")

    rankingItems.forEach((item, index) => {
      item.setAttribute("draggable", "true")

      item.addEventListener("dragstart", (e) => {
        draggedElement = e.currentTarget
        draggedElement.classList.add("opacity-50", "dragging")
        e.dataTransfer.effectAllowed = "move"
        e.dataTransfer.setData("text/plain", draggedElement.dataset.itemId)
        // Add a visual indicator
        draggedElement.style.opacity = "0.5"
      })

      item.addEventListener("dragend", (e) => {
        e.currentTarget.style.opacity = "1"
        e.currentTarget.classList.remove("opacity-50", "dragging")
        draggedElement = null
        draggedOverElement = null

        // Update ranking after drag ends
        this.isInitializing = false
        this.updateRankingDisplay()
      })

      item.addEventListener("dragover", (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = "move"

        if (!draggedElement || draggedElement === e.currentTarget) return

        // Visual feedback
        const afterElement = this.getDragAfterElement(container, e.clientY)
        const allItems = container.querySelectorAll(".ranking-item:not(.dragging)")

        allItems.forEach(item => item.classList.remove("border-primary"))

        if (afterElement == null) {
          // Will be appended to end
          const lastItem = allItems[allItems.length - 1]
          if (lastItem) lastItem.classList.add("border-primary")
        } else {
          afterElement.classList.add("border-primary")
        }
      })

      item.addEventListener("dragenter", (e) => {
        e.preventDefault()
        if (draggedElement && draggedElement !== e.currentTarget) {
          draggedOverElement = e.currentTarget
        }
      })

      item.addEventListener("dragleave", (e) => {
        e.currentTarget.classList.remove("border-primary")
      })

      item.addEventListener("drop", (e) => {
        e.preventDefault()
        e.stopPropagation()

        if (!draggedElement) return

        const afterElement = this.getDragAfterElement(container, e.clientY)

        if (afterElement == null) {
          container.appendChild(draggedElement)
        } else {
          container.insertBefore(draggedElement, afterElement)
        }

        // Clean up visual state
        draggedElement.style.opacity = "1"
        draggedElement.classList.remove("opacity-50", "dragging")
        container.querySelectorAll(".ranking-item").forEach(item => {
          item.classList.remove("border-primary")
        })

        this.isInitializing = false
        this.updateRankingDisplay()

        draggedElement = null
        draggedOverElement = null
      })
    })

    // Also handle drop on container itself
    container.addEventListener("dragover", (e) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
    })

    container.addEventListener("drop", (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (!draggedElement) return

      // Append to end if dropped on container
      container.appendChild(draggedElement)
      draggedElement.style.opacity = "1"
      draggedElement.classList.remove("opacity-50", "dragging")

      this.isInitializing = false
      this.updateRankingDisplay()

      draggedElement = null
    })

    // Initial ranking display (but don't auto-advance)
    this.updateRankingDisplay()

    // Clear initialization flag after a short delay
    setTimeout(() => {
      this.isInitializing = false
    }, 500)
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll(".ranking-item:not(.dragging)")]

    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2

      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      } else {
        return closest
      }
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  updateRankingDisplay() {
    const container = this.element.querySelector("#ranking-container")
    const display = this.element.querySelector("#ranking-display")
    if (!container || !display) return

    const items = container.querySelectorAll(".ranking-item")
    const ranking = {}
    items.forEach((item, index) => {
      const itemId = item.dataset.itemId
      if (itemId) {
        ranking[itemId] = index + 1
      }
    })

    const rankingText = Object.entries(ranking)
      .sort((a, b) => a[1] - b[1])
      .map(([id, rank]) => `${id.charAt(0).toUpperCase() + id.slice(1)} (${rank})`)
      .join(", ")

    display.textContent = rankingText || "Drag items to reorder"

    // Store answer
    const previousAnswer = this.currentAnswer
    this.currentAnswer = ranking
    this.updateNextButton()

    // Don't auto-advance ranking - user must click Next button
    // Auto-advance removed per user requirement
  }

  // Q2: Draggable allocation pie (allocation-pie controller dispatches change on connect/drag)
  sliderPieChanged(event) {
    this.isInitializing = false
    this.currentAnswer = {
      personal: event.detail.personal,
      strategic: event.detail.strategic,
      tactical: event.detail.tactical
    }
    this.updateNextButton()
  }

  // Q2: Legacy constrained sliders (kept for questions still configured with range inputs)
  initializeSliders() {
    const sliders = this.element.querySelectorAll(".slider-input")
    if (!sliders.length) return

    // Set flag to prevent auto-advance on initial load
    this.isInitializing = true

    sliders.forEach((slider) => {
      slider.addEventListener("input", () => {
        this.isInitializing = false
        this.updateSliders()
      })
    })

    // Initial update (but don't auto-advance)
    this.updateSliders()

    // Clear initialization flag after a short delay
    setTimeout(() => {
      this.isInitializing = false
    }, 500)
  }

  updateSlider(event) {
    const slider = event.target
    const sliderId = slider.dataset.sliderId
    const value = parseInt(slider.value)
    const valueDisplay = this.element.querySelector(`.slider-value[data-slider-id="${sliderId}"]`)

    if (valueDisplay) {
      valueDisplay.textContent = `${value}%`
    }

    this.updateSliders()
  }

  updateSliders() {
    const sliders = this.element.querySelectorAll(".slider-input")
    const totalDisplay = this.element.querySelector("#slider-total")
    const errorDisplay = this.element.querySelector("#slider-error")
    const summary = this.element.querySelector("#slider-summary")

    let total = 0
    const values = {}

    sliders.forEach((slider) => {
      const sliderId = slider.dataset.sliderId
      const value = parseInt(slider.value)
      values[sliderId] = value
      total += value
    })

    if (totalDisplay) {
      totalDisplay.textContent = `${total}%`
    }

    // Update summary background based on total
    if (summary) {
      if (total === 100) {
        summary.classList.remove("bg-red-50", "border-red-200")
        summary.classList.add("bg-primary-50", "border-green-200")
        if (errorDisplay) errorDisplay.classList.add("hidden")
      } else {
        summary.classList.remove("bg-primary-50", "border-green-200")
        summary.classList.add("bg-red-50", "border-red-200")
        if (errorDisplay) {
          errorDisplay.classList.remove("hidden")
          errorDisplay.textContent = `Total must equal 100% (currently ${total}%)`
        }
      }
    }

    // Store answer
    const previousAnswer = this.currentAnswer
    this.currentAnswer = values
    this.updateNextButton()

    // Don't auto-advance sliders - user must click Next button
    // Auto-advance removed per user requirement
  }

  // Q3-Q7: Scale questions
  initializeScale() {
    // Scale questions are handled by selectScore method
  }

  // Q3: Multi-select with follow-up
  initializeMultiselect() {
    // Check for existing selections and show follow-up if needed
    this.updateMultiselectFollowUp()
  }

  toggleMultiselect(event) {
    const checkbox = event.currentTarget
    const optionId = checkbox.dataset.optionId
    const isChecked = checkbox.checked

    // Update current answer
    if (!this.currentAnswer) {
      this.currentAnswer = { selected: [], primary: null }
    }
    if (!this.currentAnswer.selected) {
      this.currentAnswer.selected = []
    }

    if (isChecked) {
      if (!this.currentAnswer.selected.includes(optionId)) {
        this.currentAnswer.selected.push(optionId)
      }
    } else {
      this.currentAnswer.selected = this.currentAnswer.selected.filter(id => id !== optionId)
      // If primary was deselected, clear it
      if (this.currentAnswer.primary === optionId) {
        this.currentAnswer.primary = null
      }
    }

    this.updateMultiselectFollowUp()
    this.updateNextButton()
  }

  updateMultiselectFollowUp() {
    const followUpContainer = this.element.querySelector("#follow-up-container")
    const followUpOptions = this.element.querySelector("#follow-up-options")

    if (!followUpContainer || !followUpOptions) return

    const selected = this.currentAnswer?.selected || []

    if (selected.length > 0) {
      followUpContainer.classList.remove("hidden")

      // Get question metadata to find option labels
      const checkboxes = this.element.querySelectorAll(".multiselect-checkbox")
      const options = Array.from(checkboxes)
        .filter(cb => selected.includes(cb.dataset.optionId))
        .map(cb => {
          const label = cb.closest("label")
          const labelText = label.querySelector("h3")?.textContent || cb.dataset.optionId
          return { id: cb.dataset.optionId, label: labelText }
        })

      // Populate follow-up radio options
      followUpOptions.innerHTML = options.map(opt => `
        <label class="flex items-start p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary-50 transition-all duration-200">
          <input type="radio"
                 name="follow_up_primary"
                 class="mt-1 mr-4 w-5 h-5 text-primary border-gray-300 focus:ring-primary follow-up-radio"
                 data-option-id="${opt.id}"
                 data-action="change->team-mission-question#selectPrimary"
                 ${this.currentAnswer?.primary === opt.id ? "checked" : ""}>
          <div class="flex-1">
            <h3 class="font-semibold text-gray-900">${opt.label}</h3>
          </div>
        </label>
      `).join("")
    } else {
      followUpContainer.classList.add("hidden")
      if (this.currentAnswer) {
        this.currentAnswer.primary = null
      }
    }
  }

  selectPrimary(event) {
    const radio = event.currentTarget
    const optionId = radio.dataset.optionId

    if (!this.currentAnswer) {
      this.currentAnswer = { selected: [], primary: null }
    }
    this.currentAnswer.primary = optionId

    this.updateNextButton()
  }

  // Q4-Q7: Radio buttons
  initializeRadio() {
    const radioInputs = this.element.querySelectorAll('input[type="radio"]')
    const radioLabels = this.element.querySelectorAll('label.radio-option')

    radioInputs.forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.selectRadio(e)
      })
      radio.addEventListener('click', (e) => {
        e.stopPropagation()
        this.selectRadio(e)
      })
    })

    radioLabels.forEach(label => {
      label.addEventListener('click', (e) => {
        const radio = label.querySelector('input[type="radio"]')
        if (radio && !radio.checked) {
          radio.checked = true
          const changeEvent = new Event('change', { bubbles: true })
          radio.dispatchEvent(changeEvent)
        }
      })
    })
  }

  selectRadio(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isProcessing) return

    // Determine if we clicked on the label or the input
    let radio, labelElement, optionId

    if (event.target.type === "radio") {
      // Clicked directly on the radio input
      radio = event.target
      labelElement = radio.closest("label.radio-option")
      optionId = radio.dataset.optionId || radio.value
    } else {
      // Clicked on the label
      labelElement = event.currentTarget
      radio = labelElement.querySelector('input[type="radio"]')
      optionId = radio ? (radio.dataset.optionId || radio.value) : null
    }

    if (!radio || !optionId) return

    // Check the radio button programmatically
    radio.checked = true

    // Update visual state - remove selection from all options
    this.element.querySelectorAll(".radio-option").forEach(opt => {
      opt.classList.remove("border-primary", "bg-primary-50")
      opt.classList.add("border-gray-300")
    })

    // Add selection to the clicked option
    if (labelElement) {
      labelElement.classList.remove("border-gray-300")
      labelElement.classList.add("border-primary", "bg-primary-50")
    }

    this.currentAnswer = { selected: optionId }
    this.updateNextButton()

    setTimeout(() => {
      if (!this.isProcessing && this.currentAnswer && this.currentAnswer.selected) {
        this.nextQuestion()
      }
    }, 500)
  }

  selectScore(event) {
    if (this.isProcessing) {
      return
    }

    const button = event.currentTarget
    const score = parseInt(button.dataset.score)

    if (isNaN(score)) return

    // Remove selection from all buttons
    this.element.querySelectorAll(".score-option").forEach((btn) => {
      btn.classList.remove("border-primary", "bg-primary-50", "ring-2", "ring-primary")
      btn.classList.add("border-gray-300", "bg-white")
    })

    // Add selection to clicked button
    button.classList.remove("border-gray-300", "bg-white")
    button.classList.add("border-primary", "bg-primary-50", "ring-2", "ring-primary")

    // Store answer
    this.currentAnswer = { score: score }
    this.updateNextButton()

    // Auto-advance for scale questions after a short delay
    if (this.questionTypeValue === "scale") {
      setTimeout(() => {
        if (!this.isProcessing && this.currentAnswer && this.currentAnswer.score !== undefined) {
          this.nextQuestion()
        }
      }, 500) // 500ms delay to show the selection
    }
  }

  updateNextButton() {
    const nextButton = this.element.querySelector("#next-question-btn")
    if (!nextButton) return

    let canProceed = false

    if (this.questionTypeValue === "ranking") {
      if (this.currentAnswer) {
        const itemCount = Object.keys(this.currentAnswer).length
        const expectedCount = this.element.querySelectorAll(".ranking-item").length
        canProceed = itemCount === expectedCount && itemCount > 0
      }
    } else if (this.questionTypeValue === "slider") {
      if (this.currentAnswer) {
        const total = Object.values(this.currentAnswer).reduce((sum, val) => sum + val, 0)
        canProceed = total === 100
      }
    } else if (this.questionTypeValue === "scale") {
      canProceed = this.currentAnswer !== null && this.currentAnswer.score !== undefined
    } else if (this.questionTypeValue === "multiselect") {
      if (this.currentAnswer) {
        const hasSelections = this.currentAnswer.selected && this.currentAnswer.selected.length > 0
        const hasPrimary = this.currentAnswer.primary !== null && this.currentAnswer.primary !== undefined
        canProceed = hasSelections && hasPrimary
      }
    } else if (this.questionTypeValue === "radio") {
      canProceed = this.currentAnswer !== null &&
        this.currentAnswer !== undefined &&
        this.currentAnswer.selected !== undefined &&
        this.currentAnswer.selected !== null &&
        this.currentAnswer.selected !== ""
    }

    nextButton.disabled = !canProceed
  }

  scrollQuestionToTop(parent) {
    if (!parent) return
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
      const newContainer = parent.firstElementChild
      const scrollable = newContainer?.querySelector(".overflow-y-auto")
      if (scrollable) scrollable.scrollTo({ top: 0, behavior: "smooth" })
    })
  }

  async nextQuestion() {
    if (this.isProcessing) return
    if (!this.currentAnswer) {
      alert("Please provide an answer before continuing.")
      return
    }

    // Validate Q2 sliders sum to 100
    if (this.questionTypeValue === "slider") {
      const total = Object.values(this.currentAnswer).reduce((sum, val) => sum + val, 0)
      if (total !== 100) {
        alert("Time allocation must sum to exactly 100%")
        return
      }
    }

    this.isProcessing = true
    const nextButton = this.element.querySelector("#next-question-btn")
    if (nextButton) {
      nextButton.disabled = true
      nextButton.textContent = "Processing..."
    }

    try {
      const url = `/teams/${this.teamIdValue}/team_mission_profile/next_question`
      const formData = new FormData()
      formData.append("question_id", this.questionIdValue)
      formData.append("response_data", JSON.stringify(this.currentAnswer))

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData,
        redirect: "manual"
      })

      // Handle redirects (302) - this happens when it's the last question
      if (response.status === 302 || response.status === 0) {
        // Get redirect URL from Location header
        const redirectUrl = response.headers.get("Location")
        if (redirectUrl) {
          window.location.href = redirectUrl
        } else {
          // Fallback: redirect to the profile page
          window.location.href = `/teams/${this.teamIdValue}/team_mission_profile`
        }
        return
      }

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType && contentType.includes("text/html")) {
          const html = await response.text()
          const parent = this.element.parentElement
          const oldElement = this.element
          oldElement.outerHTML = html
          this.scrollQuestionToTop(parent)
        } else {
          const data = await response.json()
          if (data.error) {
            alert(data.error)
            this.isProcessing = false
            if (nextButton) {
              nextButton.disabled = false
              nextButton.textContent = "Next →"
            }
          } else if (data.redirect) {
            window.location.href = data.redirect
          }
        }
      } else {
        // Try to get error message from response
        let errorMessage = `An error occurred: ${response.status}`
        try {
          const errorText = await response.text()
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText)
              errorMessage = errorData.error || errorData.message || errorMessage
            } catch {
              // If not JSON, use the text as is if it's short
              if (errorText.length < 200) {
                errorMessage = errorText
              }
            }
          }
        } catch (e) {
          console.error("Error reading response:", e)
        }

        console.error("Error response:", response.status, errorMessage)
        alert(errorMessage)
        this.isProcessing = false
        if (nextButton) {
          nextButton.disabled = false
          nextButton.textContent = "Next →"
        }
      }
    } catch (error) {
      console.error("Error advancing to next question:", error)
      alert("An error occurred. Please try again.")
      this.isProcessing = false
      if (nextButton) {
        nextButton.disabled = false
        nextButton.textContent = "Next →"
      }
    }
  }

  async previousQuestion() {
    if (this.isProcessing) return

    this.isProcessing = true

    try {
      const url = `/teams/${this.teamIdValue}/team_mission_profile/previous_question`
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        redirect: "manual"
      })

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType && contentType.includes("text/html")) {
          const html = await response.text()
          const parent = this.element.parentElement
          this.element.outerHTML = html
          this.scrollQuestionToTop(parent)
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error("Error loading previous question:", error)
      this.isProcessing = false
    }
  }
}
